import { useCallback } from 'react';
import { useDispatch, useSelector } from 'core/common/hooks';
import { evalCondition } from 'core/common/utils/evalCondition';
import { EmailFlowAttributes, isEmailFlow } from 'core/flow/entities';
import { getConditionAttributes } from 'core/funnel/store/selectors';
import { addReading } from 'core/user/store/actions';

export const useEmailFlow = () => {
  const flowConditions = useSelector(getConditionAttributes);
  const dispatch = useDispatch();

  const getEmailFlow = useCallback(
    (emailFlowAttributes: EmailFlowAttributes['emailFlowAttributes']): string => {
      if (!emailFlowAttributes || !emailFlowAttributes.emailFlow) {
        return '';
      }

      const emailFlow = emailFlowAttributes.emailFlow.find(isEmailFlow);

      if (!emailFlow) {
        return '';
      }

      const emailFlowNamesWithoutCondition = emailFlow.attributes
        .filter((attribute) => !attribute.condition)
        .map((attribute) => attribute.name);

      const emailFlowNamesWithCondition = emailFlow.attributes
        .filter((attribute) => !!attribute.condition)
        .filter((attribute) => [flowConditions].find(evalCondition(attribute.condition)))
        .map((attribute) => attribute.name);

      if (emailFlowNamesWithCondition.length === 1) {
        return emailFlowNamesWithCondition[0];
      }

      if (emailFlowNamesWithCondition.length > 1) {
        return '';
      }

      if (emailFlowNamesWithoutCondition.length === 1) {
        return emailFlowNamesWithoutCondition[0];
      }

      return '';
    },
    // Ignoring store as a dependency, because conditionAttributes are not dynamic
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const addUserReading = useCallback(
    (attrs: EmailFlowAttributes['emailFlowAttributes']): void => {
      dispatch(addReading(attrs));
    },
    [dispatch],
  );

  return { getEmailFlow, addUserReading };
};
